import type {
  AutocompleteData,
  ColoredMaterialResult,
} from "@color-finder/color-finder-api/models";
import type { ColorFilterValue } from "components/filter/color-filter/ColorFilter";
import type { Dataset } from "models/colors";
import { useDataset } from "providers/DatasetProvider";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { useContext, useEffect, useMemo, useRef } from "react";
import type { ColorCieLab } from "service/color-math";
import { appendDatasetParam } from "service/utils/dataset";
import { trackSearch } from "../service/tracking";
import { getAuthenticatedAxios } from "./authentication";
import type { FilterEntry, FilterType } from "./filters";
import { type RequestState, useAuthenticatedRequest } from "./request";
import type { SortEntry } from "./sort";

const COLOR_SEARCH_URL = "/v0/color-search/";
const REFERENCE_SEARCH_URL = "/v0/color-search/reference";

const buildAutocompleteConfig =
  (propertyName: string) =>
  (filter: FilterEntry<Array<AutocompleteData>>, params: URLSearchParams) =>
    filter.value.sort().forEach((f) => {
      params.append(propertyName, f.value);
    });

/**
 * Appends a color filter's params. `labParam` selects which query key carries
 * the entered Lab value: `cie_lab` for the normal color search, or
 * `filter_cie_lab` for the reference search (where `cie_lab` is reserved for the
 * extracted image color and this is the secondary color to intersect with).
 */
const appendColorFilterParams = (
  filter: FilterEntry<ColorFilterValue>,
  params: URLSearchParams,
  labParam: "cie_lab" | "filter_cie_lab",
) => {
  if (filter.value.color !== undefined) {
    params.append(labParam, filter.value.color.value.join());
  }
  if (filter.value.finalColorCode) {
    filter.value.finalColorCode.sort().forEach((f) => {
      params.append("finalColorCode", f.value);
    });
  }
  if (filter.value.yearOfDevelopment) {
    filter.value.yearOfDevelopment.sort().forEach((f) => {
      params.append("yearOfDevelopment", f.value);
    });
  }
  if (filter.value.preliminaryColorCode) {
    filter.value.preliminaryColorCode.sort().forEach((f) => {
      params.append("preliminaryColorCode", f.value);
    });
  }
};

const apiFilterConfig: Record<
  FilterType,
  (filter: FilterEntry, params: URLSearchParams) => void
> = {
  hasDigitalSample: (filter, params) =>
    params.set("hasDigitalSample", filter.value),
  hasLevel3Data: (filter, params) => params.set("hasLevel3Data", filter.value),
  isColorSystem: (filter, params) =>
    params.set(
      "colorSystem",
      filter.value === "ncs"
        ? "NCS"
        : filter.value === "ral_classic"
          ? "RAL CLASSIC"
          : "RAL DESIGN SYSTEM plus",
    ),
  colorSystemCode: (filter, params) =>
    params.set("colorSystemCode", filter.value),
  colorStatus: buildAutocompleteConfig("status"),
  substanceList: buildAutocompleteConfig("substanceList"),
  grade: buildAutocompleteConfig("grade"),
  brand: buildAutocompleteConfig("brand"),
  gradeShortName: buildAutocompleteConfig("gradeShortName"),
  gradeNumber: buildAutocompleteConfig("gradeNumber"),
  gradeProperty: buildAutocompleteConfig("gradeProperty"),
  colorEffect: buildAutocompleteConfig("colorEffect"),
  colorTag: buildAutocompleteConfig("colorTag"),
  colorGroup: buildAutocompleteConfig("colorGroup"),
  opacity: buildAutocompleteConfig("opacity"),
  cdc: buildAutocompleteConfig("cdc"),
  oemName: buildAutocompleteConfig("oemName"),
  customerName: buildAutocompleteConfig("customerName"),
  uvp: buildAutocompleteConfig("uvp"),
  color: (filter: FilterEntry<ColorFilterValue>, params: URLSearchParams) =>
    appendColorFilterParams(filter, params, "cie_lab"),
  industry: buildAutocompleteConfig("industry"),
  applicationClass: buildAutocompleteConfig("applicationClass"),
  application: buildAutocompleteConfig("application"),
  texture: buildAutocompleteConfig("texture"),
};

/** Named so a trailing `undefined` placeholder can't be mistaken for the dataset. */
interface DatasetOptions {
  dataset?: Dataset;
}

const appendCommonParams = (
  params: URLSearchParams,
  sort?: Array<SortEntry>,
  page?: number,
  pageSize?: number,
  externalViewVisible?: boolean,
  { dataset = "original" }: DatasetOptions = {},
) => {
  if (externalViewVisible) {
    params.set("isExternalView", externalViewVisible.toString());
  }
  appendDatasetParam(params, dataset);
  if (page !== undefined) {
    params.set("page", page.toString());
  }
  if (sort !== undefined) {
    sort
      .map((value) => (value.direction === "asc" ? "" : "-") + value.property)
      .forEach((value) => {
        params.append("sort", value);
      });
  }
  if (pageSize !== undefined) {
    params.set("limit", pageSize.toString());
  }
  params.set("maxDataAccessLimit", "500");
};

export function filtersToParams(
  filters: Array<FilterEntry>,
  sort?: Array<SortEntry>,
  page?: number,
  pageSize?: number,
  externalViewVisible?: boolean,
  options: DatasetOptions = {},
) {
  const params = new URLSearchParams();
  filters.forEach((filter) => {
    apiFilterConfig[filter.type](filter, params);
  });
  appendCommonParams(
    params,
    sort,
    page,
    pageSize,
    externalViewVisible,
    options,
  );
  return { params };
}

/**
 * Builds params for the reference search endpoint (`/v0/color-search/reference`),
 * which intersects the extracted image color with a secondary user color/system.
 *
 * - `referenceCieLab` (the extracted image color) is sent as `cie_lab` — the
 *   primary deltaE constraint that always applies.
 * - Any user-entered `color` filter is sent as `filter_cie_lab` (CIE-Lab, or
 *   LCh already converted to Lab upstream) so it forms a second deltaE box
 *   instead of colliding with the image color on the same `cie_lab` key.
 * - When `referenceCieLab` is absent (the reference image was discarded but a
 *   Color:CIE-Lab filter survives — see CX-2908's confirmDiscard/confirmResetAll),
 *   the endpoint has no primary reference at all and 400s on a bare
 *   `filter_cie_lab` (see `reference-search-function.handler.ts`). The user's
 *   Lab value is promoted to `cie_lab` in that case so it becomes the primary
 *   reference instead of a secondary filter with nothing to intersect against.
 * - All other filters (colorSystem, colorGroup, opacity, grade, ...) reuse the
 *   shared {@link apiFilterConfig} mapping unchanged.
 */
export function referenceSearchToParams(
  referenceCieLab: ColorCieLab | undefined,
  filters: Array<FilterEntry>,
  sort?: Array<SortEntry>,
  page?: number,
  pageSize?: number,
  externalViewVisible?: boolean,
  options: DatasetOptions = {},
) {
  const params = new URLSearchParams();
  const labParam: "cie_lab" | "filter_cie_lab" =
    referenceCieLab !== undefined ? "filter_cie_lab" : "cie_lab";
  if (referenceCieLab !== undefined) {
    params.append("cie_lab", referenceCieLab.join());
  }
  filters.forEach((filter) => {
    if (filter.type === "color") {
      appendColorFilterParams(
        filter as FilterEntry<ColorFilterValue>,
        params,
        labParam,
      );
    } else {
      apiFilterConfig[filter.type](filter, params);
    }
  });
  appendCommonParams(
    params,
    sort,
    page,
    pageSize,
    externalViewVisible,
    options,
  );
  return { params };
}

/**
 * Stable key for a color-search request, used to decide whether a prefetched
 * result (see `initialData`/`initialKey` on {@link useColoredMaterials}) still
 * applies to the current params. Built from the same `filtersToParams` output
 * the request itself uses, so the prefetch and the live hook agree byte-for-byte.
 */
export function buildColorSearchKey(
  filters: Array<FilterEntry>,
  sort?: Array<SortEntry>,
  page?: number,
  pageSize?: number,
  externalViewVisible?: boolean,
  options: DatasetOptions = {},
): string {
  return filtersToParams(
    filters,
    sort,
    page,
    pageSize,
    externalViewVisible,
    options,
  ).params.toString();
}

/**
 * One-shot color search outside the hook lifecycle, used to prefetch the first
 * results during the loading screen so the results view can paint immediately.
 * Mirrors the request `useColoredMaterials` issues (same endpoint, same
 * `filtersToParams`) and returns both the data and the params key so the caller
 * can seed `useColoredMaterials` via `initialData`/`initialKey`.
 */
export async function prefetchColorSearch(
  filters: Array<FilterEntry>,
  sort: Array<SortEntry> | undefined,
  page: number | undefined,
  pageSize: number | undefined,
  externalViewVisible: boolean | undefined,
  options: DatasetOptions = {},
): Promise<{ result: ColoredMaterialResult; key: string }> {
  const config = filtersToParams(
    filters,
    sort,
    page,
    pageSize,
    externalViewVisible,
    options,
  );
  const { axios } = getAuthenticatedAxios();
  const response = await axios.get<ColoredMaterialResult>(
    COLOR_SEARCH_URL,
    config,
  );
  return { result: response.data, key: config.params.toString() };
}

/**
 * Reference-search variant of {@link buildColorSearchKey}. Built from
 * {@link referenceSearchToParams} so the prefetch seed and the live reference
 * request agree byte-for-byte.
 */
export function buildReferenceSearchKey(
  referenceCieLab: ColorCieLab | undefined,
  filters: Array<FilterEntry>,
  sort?: Array<SortEntry>,
  page?: number,
  pageSize?: number,
  externalViewVisible?: boolean,
  options: DatasetOptions = {},
): string {
  return referenceSearchToParams(
    referenceCieLab,
    filters,
    sort,
    page,
    pageSize,
    externalViewVisible,
    options,
  ).params.toString();
}

/**
 * Reference-search variant of {@link prefetchColorSearch}. Hits
 * `/v0/color-search/reference` with the extracted image color as `cie_lab` so
 * the results view can be seeded on first paint.
 */
export async function prefetchReferenceSearch(
  referenceCieLab: ColorCieLab | undefined,
  filters: Array<FilterEntry>,
  sort: Array<SortEntry> | undefined,
  page: number | undefined,
  pageSize: number | undefined,
  externalViewVisible: boolean | undefined,
  options: DatasetOptions = {},
): Promise<{ result: ColoredMaterialResult; key: string }> {
  const config = referenceSearchToParams(
    referenceCieLab,
    filters,
    sort,
    page,
    pageSize,
    externalViewVisible,
    options,
  );
  const { axios } = getAuthenticatedAxios();
  const response = await axios.get<ColoredMaterialResult>(
    REFERENCE_SEARCH_URL,
    config,
  );
  return { result: response.data, key: config.params.toString() };
}

/**
 * Fetches the color-search results for the given filters/sort/pagination,
 * re-running whenever those inputs change.
 *
 * Optionally seeds the first render with a result prefetched during the loading
 * screen (CX-2387) so the results view paints without a second fetch. The seed
 * is applied only while the live params still match `initialKey`; the moment the
 * user changes the color, filter, sort, page, or region the params diverge, the
 * hook falls back to a normal request, and it never re-seeds.
 *
 * @param initialData Prefetched result to return on first render. Only used
 *   while `initialKey` matches the live params; ignored once they diverge. Build
 *   it with {@link prefetchColorSearch}, which returns both the data and the key.
 * @param initialKey Stable params key the `initialData` was fetched with, from
 *   {@link buildColorSearchKey}. Must be built from the same
 *   filters/sort/page/pageSize the hook will first render with, or the seed
 *   never applies.
 */
export function useColoredMaterials(
  filters: Array<FilterEntry>,
  sort?: Array<SortEntry>,
  page?: number,
  pageSize?: number,
  shouldLoad = true,
  initialData?: ColoredMaterialResult,
  initialKey?: string,
): RequestState<ColoredMaterialResult> {
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const externalViewVisible = settings?.externalViewVisible;
  const { dataset } = useDataset();

  const config = useMemo(
    () =>
      filtersToParams(filters, sort, page, pageSize, externalViewVisible, {
        dataset,
      }),
    [filters, sort, page, pageSize, externalViewVisible, dataset],
  );

  // Seed the first render with a result prefetched during the loading screen so
  // the results view paints immediately. The seed only applies while the live
  // params still match `initialKey`; once the user changes anything (color,
  // filter, sort, page, region) the params diverge and never re-seed, so all
  // subsequent searches behave exactly as before.
  const hasDivergedRef = useRef(false);
  const currentKey = config.params.toString();
  if (initialKey !== undefined && currentKey !== initialKey) {
    hasDivergedRef.current = true;
  }
  const isSeeded =
    !hasDivergedRef.current &&
    initialData !== undefined &&
    initialKey !== undefined &&
    currentKey === initialKey;

  const [result, error, isLoading] =
    useAuthenticatedRequest<ColoredMaterialResult>(
      COLOR_SEARCH_URL,
      config,
      shouldLoad && !isSeeded,
    );
  // Track even when seeded: the prefetched first search is a real search and
  // was tracked in the pre-change flow. With isLoading=false from the seed, the
  // tracking effect fires once on mount, matching the old behavior.
  useSearchTracking(
    filters,
    page,
    pageSize,
    isSeeded ? false : isLoading,
    error,
    shouldLoad,
  );

  if (isSeeded) {
    return [initialData, undefined, false];
  }
  return [result, error, isLoading];
}

/**
 * Reference-search counterpart of {@link useColoredMaterials} for the image
 * "Upload & Match" results view. Sends the extracted image color as `cie_lab`
 * and the user's secondary color/system filters (via `filter_cie_lab`,
 * `colorSystem`, `colorGroup`, ...) to `/v0/color-search/reference`, so the
 * backend returns the intersection of both constraints instead of two
 * conflicting searches. Shares the same prefetch-seed contract.
 */
export function useReferenceMaterials(
  referenceCieLab: ColorCieLab | undefined,
  filters: Array<FilterEntry>,
  sort?: Array<SortEntry>,
  page?: number,
  pageSize?: number,
  shouldLoad = true,
  initialData?: ColoredMaterialResult,
  initialKey?: string,
): RequestState<ColoredMaterialResult> {
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const externalViewVisible = settings?.externalViewVisible;
  const { dataset } = useDataset();

  const config = useMemo(
    () =>
      referenceSearchToParams(
        referenceCieLab,
        filters,
        sort,
        page,
        pageSize,
        externalViewVisible,
        { dataset },
      ),
    [
      referenceCieLab,
      filters,
      sort,
      page,
      pageSize,
      externalViewVisible,
      dataset,
    ],
  );

  const hasDivergedRef = useRef(false);
  const currentKey = config.params.toString();
  if (initialKey !== undefined && currentKey !== initialKey) {
    hasDivergedRef.current = true;
  }
  const isSeeded =
    !hasDivergedRef.current &&
    initialData !== undefined &&
    initialKey !== undefined &&
    currentKey === initialKey;

  const [result, error, isLoading] =
    useAuthenticatedRequest<ColoredMaterialResult>(
      REFERENCE_SEARCH_URL,
      config,
      shouldLoad && !isSeeded,
    );
  useSearchTracking(
    filters,
    page,
    pageSize,
    isSeeded ? false : isLoading,
    error,
    shouldLoad,
    // The extracted image color is a real search constraint even when the user
    // adds no secondary filters, so keep tracking image-only reference searches.
    referenceCieLab !== undefined,
  );

  if (isSeeded) {
    return [initialData, undefined, false];
  }
  return [result, error, isLoading];
}

const useSearchTracking = (
  filters: Array<FilterEntry>,
  page: number | undefined,
  pageSize: number | undefined,
  isLoading: boolean,
  error: Error | undefined,
  shouldLoad: boolean,
  hasReference = false,
) => {
  const lastTrackedKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!shouldLoad || isLoading || error) return;
    if (filters.length === 0 && !hasReference) return;
    const eventData = buildSearchEventData(filters, page, pageSize);
    const key = JSON.stringify(eventData);
    if (lastTrackedKey.current === key) return;
    lastTrackedKey.current = key;
    const { axios } = getAuthenticatedAxios();
    trackSearch(axios, eventData);
  }, [filters, page, pageSize, isLoading, error, shouldLoad, hasReference]);
};

const extractFilterValues = (
  filters: Array<FilterEntry>,
  type: FilterType,
): string[] | undefined => {
  const entry = filters.find((f) => f.type === type);
  if (!entry) return undefined;
  const value = entry.value as unknown;
  if (Array.isArray(value)) {
    return (value as Array<AutocompleteData>)
      .map((v) => v.value)
      .filter(Boolean);
  }
  return undefined;
};

const extractScalar = (
  filters: Array<FilterEntry>,
  type: FilterType,
): string | undefined => {
  const entry = filters.find((f) => f.type === type);
  if (!entry || entry.value === undefined || entry.value === null) {
    return undefined;
  }
  return String(entry.value);
};

const buildSearchEventData = (
  filters: Array<FilterEntry>,
  page: number | undefined,
  pageSize: number | undefined,
) => {
  const colorFilter = filters.find((f) => f.type === "color")?.value as
    | ColorFilterValue
    | undefined;
  return {
    queryParameters: {
      finalColorCode: colorFilter?.finalColorCode?.map((f) => f.value),
      preliminaryColorCode: colorFilter?.preliminaryColorCode?.map(
        (f) => f.value,
      ),
      yearOfDevelopment: colorFilter?.yearOfDevelopment?.map((f) => f.value),
      hasDigitalSample: extractScalar(filters, "hasDigitalSample"),
      hasLevel3Data: extractScalar(filters, "hasLevel3Data"),
      isColorSystem: extractScalar(filters, "isColorSystem"),
      colorSystemCode: extractScalar(filters, "colorSystemCode"),
      colorStatus: extractFilterValues(filters, "colorStatus"),
      substanceList: extractFilterValues(filters, "substanceList"),
      grade: extractFilterValues(filters, "grade"),
      brand: extractFilterValues(filters, "brand"),
      gradeShortName: extractFilterValues(filters, "gradeShortName"),
      gradeNumber: extractFilterValues(filters, "gradeNumber"),
      gradeProperty: extractFilterValues(filters, "gradeProperty"),
      // Filter key is singular ("colorEffect"), tracking payload is plural ("colorEffects").
      colorEffects: extractFilterValues(filters, "colorEffect"),
      colorTag: extractFilterValues(filters, "colorTag"),
      colorGroup: extractFilterValues(filters, "colorGroup"),
      opacity: extractFilterValues(filters, "opacity"),
      cdc: extractFilterValues(filters, "cdc"),
      oemName: extractFilterValues(filters, "oemName"),
      customerName: extractFilterValues(filters, "customerName"),
      uvp: extractFilterValues(filters, "uvp"),
      industry: extractFilterValues(filters, "industry"),
      applicationClass: extractFilterValues(filters, "applicationClass"),
      application: extractFilterValues(filters, "application"),
      texture: extractFilterValues(filters, "texture"),
    },
    paginationParameters: {
      page,
      limit: pageSize,
    },
  };
};
